import React from 'react';
import { Box, Typography, Button } from '@mui/material';

const styles = {
  textPrimary: '#0f172a',
  textSecondary: '#475569',
  accent: '#4b57AA',
  gradientStart: '#4b57AA',
  gradientMid: '#7a82d8',
  gradientEnd: '#e8a87c',
  headingFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 600 },
  bodyFont: { fontFamily: "'Raleway', sans-serif", fontWeight: 400 },
};

// Exact content from pricing page – Starter and Pro only (used for upgrade prompts)
const STARTER_CARD = {
  id: 'starter',
  type: 'starter',
  name: 'STARTER',
  tagline: 'Launch with confidence',
  priceMonthly: 149,
  priceYearly: 1300,
  limits:
    '2.5M monthly events, 5K API calls, 5 seats. Includes 2 saved dashboards, custom cohorts, 2K heatmap points, and 10 UTM short links.',
  cta: 'Choose Starter',
  preferredBy: 'Early-stage startups, launched dApps validating product-market fit',
  popular: false,
  note: null,
};

const PRO_CARD = {
  id: 'pro',
  type: 'pro',
  name: 'PRO',
  tagline: 'Scale without limits',
  priceMonthly: 499,
  priceYearly: 4200,
  limits:
    '5M monthly events, 50K API calls, unlimited seats. Includes 1,500 Wallet Credits, unlimited dashboards, data export, CQ Intelligence AI, and priority support.',
  cta: 'Choose Pro',
  preferredBy: 'Growing teams optimizing for retention and data-driven growth',
  popular: true,
  note: '30-day free trial available (book a demo)',
};

/**
 * Renders upgrade cards for the Billing tab.
 * - currentPlan 'free' → show Starter + Pro
 * - currentPlan 'starter' → show Pro only
 * - currentPlan 'pro' | 'enterprise' → show nothing
 */
const UpgradeCards = ({ currentPlan, billingCycle, onBillingCycleChange, onSelectPlan, loading }) => {
  const planKey = (currentPlan || 'free').toLowerCase();
  const showStarter = planKey === 'free';
  const showPro = planKey === 'free' || planKey === 'starter';
  const cards = [];
  if (showStarter) cards.push(STARTER_CARD);
  if (showPro) cards.push(PRO_CARD);
  if (cards.length === 0) return null;

  const isTwoCards = cards.length === 2;
  const gapPx = 16; // theme spacing 2 = 16px
  const cardFlex = isTwoCards ? `0 0 calc(50% - ${gapPx / 2}px)` : '0 0 50%';

  return (
    <Box sx={{ mb: 3, width: '100%' }}>
      <Typography
        variant="subtitle1"
        sx={{
          ...styles.headingFont,
          color: styles.textPrimary,
          fontSize: '1rem',
          mb: 2,
          textAlign: 'center',
        }}
      >
        Upgrade for more access
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        {cards.map((plan) => {
          const isPopular = plan.popular;
          const price =
            billingCycle === 'annual'
              ? `$${plan.priceYearly}/year`
              : `$${plan.priceMonthly}/month`;

          return (
            <Box
              key={plan.id}
              sx={{
                position: 'relative',
                borderRadius: '16px',
                p: isPopular ? '2px' : 0,
                margin: isPopular ? '-2px' : 0,
                background: isPopular
                  ? `linear-gradient(to right, ${styles.gradientStart}, ${styles.gradientMid}, ${styles.gradientEnd})`
                  : 'transparent',
                boxShadow: isPopular ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
                flex: cardFlex,
                minWidth: { xs: '100%', sm: 280 },
                maxWidth: { xs: '100%', sm: isTwoCards ? 'none' : '50%' },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  height: '100%',
                  minHeight: 0,
                  p: 2,
                  borderRadius: isPopular ? '14px' : '16px',
                  backgroundColor: isPopular ? '#fff' : 'rgba(255,255,255,0.6)',
                  border: isPopular ? 'none' : '1px solid rgba(255,255,255,0.5)',
                  boxShadow: isPopular ? 'none' : '0 4px 16px rgba(0,0,0,0.06)',
                  backdropFilter: 'blur(12px)',
                  '&:hover': {
                    boxShadow: isPopular ? 'none' : '0 8px 24px rgba(0,0,0,0.1)',
                  },
                }}
              >
                {isPopular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        borderRadius: '9999px',
                        backgroundColor: styles.accent,
                        color: '#fff',
                        px: 1.5,
                        py: 0.5,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      ⭐ MOST POPULAR
                    </Box>
                  </Box>
                )}
                <Box sx={{ flex: '1 1 0', minWidth: 0, pr: { md: 2 } }}>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        ...styles.headingFont,
                        color: styles.textPrimary,
                        fontSize: '1.125rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {plan.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        ...styles.bodyFont,
                        color: styles.textSecondary,
                        fontSize: '0.875rem',
                        mt: 0.25,
                      }}
                    >
                      {plan.tagline}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      ...styles.headingFont,
                      color: styles.textPrimary,
                      fontSize: '1.375rem',
                    }}
                  >
                    {price}
                  </Typography>
                  {onBillingCycleChange && (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => onBillingCycleChange(billingCycle === 'annual' ? 'monthly' : 'annual')}
                      sx={{
                        marginTop: 0.5,
                        padding: 0,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        ...styles.bodyFont,
                        fontSize: '0.75rem',
                        color: styles.textSecondary,
                        textDecoration: 'underline',
                        '&:hover': { color: styles.textPrimary },
                      }}
                    >
                      {billingCycle === 'annual' ? 'Switch to Monthly' : 'Switch to Annual (Save 15%)'}
                    </Box>
                  )}
                </Box>
                <Box sx={{ flex: '1.2 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      ...styles.bodyFont,
                      color: styles.textSecondary,
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      mb: 1,
                      flex: 1,
                    }}
                  >
                    {plan.limits}
                  </Typography>
                  {plan.note && (
                    <Typography
                      variant="body2"
                      sx={{
                        ...styles.bodyFont,
                        color: styles.textSecondary,
                        fontSize: '0.75rem',
                        fontStyle: 'italic',
                        mb: 1,
                      }}
                    >
                      {plan.note}
                    </Typography>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    onClick={() => onSelectPlan({ type: plan.type })}
                    sx={{
                      borderRadius: '9999px',
                      py: 1,
                      mt: 'auto',
                      textTransform: 'none',
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      backgroundColor: styles.textPrimary,
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(15,23,42,0.2)',
                      '&:hover': {
                        backgroundColor: '#1e293b',
                        boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
                      },
                    }}
                  >
                    {plan.cta}
                  </Button>
                  {plan.preferredBy && (
                    <Typography
                      variant="body2"
                      sx={{
                        ...styles.bodyFont,
                        color: styles.textSecondary,
                        fontSize: '0.7rem',
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: '1px solid rgba(0,0,0,0.08)',
                      }}
                    >
                      Preferred by: {plan.preferredBy}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default UpgradeCards;
